/**
 * Single source of truth for all 78 tarot cards (Finnish course content).
 * Built from tarotCardDescriptions.fi.js + tarotCards.js. One stable id per card.
 */

import { tarotCardDescriptions } from './tarotCardDescriptions.fi';
import {
  majorArcana,
  getCardImageUrl,
  getMinorArcanaImageUrl
} from './tarotCards';

const RANK_ORDER = ['ace', '02', '03', '04', '05', '06', '07', '08', '09', '10', 'page', 'knight', 'queen', 'king'];
const RANK_NAMES_FI = {
  ace: 'Ässä',
  '02': 'Kakkonen',
  '03': 'Kolmonen',
  '04': 'Nelonen',
  '05': 'Viitonen',
  '06': 'Kuutonen',
  '07': 'Seitsemän',
  '08': 'Kahdeksan',
  '09': 'Yhdeksän',
  '10': 'Kymmenen',
  page: 'Lähetti',
  knight: 'Ritari',
  queen: 'Kuningatar',
  king: 'Kuningas'
};
const SUIT_NAMES_FI = { wands: 'Sauvat', cups: 'Maljat', swords: 'Miekat', pentacles: 'Lantit' };

function majorIdFromName(name) {
  return name.toLowerCase().replace(/\s+/g, '-');
}

function toTitle(s) {
  return String(s).replace(/\b\w/g, c => c.toUpperCase());
}

function buildContent(desc) {
  if (!desc) return {};
  return {
    imagery: desc.imagery || '',
    coreMeaning: desc.meaning || '',
    shadow: desc.shadow || '',
    readingThemes: Array.isArray(desc.readingThemes) ? desc.readingThemes : [],
    notes: desc.reflection || ''
  };
}

/** Major Arcana (22) from majorArcana + tarotCardDescriptions */
const majorCards = majorArcana.map(m => {
  const id = majorIdFromName(m.name);
  const desc = tarotCardDescriptions[id];
  return {
    id,
    name: { fi: m.nameFi, en: m.name },
    arcana: 'major',
    number: m.number,
    suit: null,
    content: buildContent(desc)
  };
});

/** Minor Arcana (56): all suits × ranks */
const minorCards = [];
['wands', 'cups', 'swords', 'pentacles'].forEach(suit => {
  RANK_ORDER.forEach(rankKey => {
    const id = `${suit}-${rankKey}`;
    const desc = tarotCardDescriptions[id];
    const nameFi = `${RANK_NAMES_FI[rankKey]} (${SUIT_NAMES_FI[suit]})`;
    const nameEn = `${toTitle(rankKey)} of ${toTitle(suit)}`;
    minorCards.push({
      id,
      name: { fi: nameFi, en: nameEn },
      arcana: 'minor',
      number: rankKey,
      suit,
      rankKey,
      content: buildContent(desc)
    });
  });
});

/** All 78 cards: major first (by number), then minor (by suit, then rank order). */
export const allCards = [
  ...majorCards.sort((a, b) => a.number - b.number),
  ...minorCards.sort((a, b) => {
    if (a.suit !== b.suit) return ['wands', 'cups', 'swords', 'pentacles'].indexOf(a.suit) - ['wands', 'cups', 'swords', 'pentacles'].indexOf(b.suit);
    return RANK_ORDER.indexOf(a.rankKey) - RANK_ORDER.indexOf(b.rankKey);
  })
];

/** Major Arcana in numerical order (0–21). */
export function getMajorCards() {
  return allCards.filter(c => c.arcana === 'major').sort((a, b) => a.number - b.number);
}

/** Minor Arcana for one suit, ordered: ace → 10, then page, knight, queen, king. */
export function getMinorCardsBySuit(suit) {
  return allCards.filter(c => c.arcana === 'minor' && c.suit === suit).sort((a, b) =>
    RANK_ORDER.indexOf(a.rankKey) - RANK_ORDER.indexOf(b.rankKey)
  );
}

/** Get one card by id. */
export function getCardById(id) {
  return allCards.find(c => c.id === id) || null;
}

/**
 * Return a card shape suitable for the course modal (and /tarot compatibility).
 * Attaches imageUrl, nameFi, name, and for major: upright/reversed from tarotCards.js.
 */
export function getDisplayCard(card) {
  if (!card) return null;
  if (card.arcana === 'major') {
    const major = majorArcana.find(m => majorIdFromName(m.name) === card.id);
    if (!major) return { ...card, name: card.name.en, nameFi: card.name.fi, number: card.number, imageUrl: null, content: card.content };
    return {
      name: major.name,
      nameFi: card.name.fi,
      number: major.number,
      imageUrl: getCardImageUrl(major.name),
      upright: major.upright,
      reversed: major.reversed,
      content: card.content
    };
  }
  return {
    isMinor: true,
    suit: card.suit,
    rankKey: card.rankKey,
    name: card.name.en,
    nameFi: card.name.fi,
    imageUrl: getMinorArcanaImageUrl(card.suit, card.rankKey),
    content: card.content
  };
}
